import { useState, useRef, useEffect } from 'react';
import { FiSend, FiPaperclip, FiX } from 'react-icons/fi';
import { useChat } from '../../contexts/ChatContext';
import { saveDraft, loadDraft } from '../../utils/chatSession';
import { cn } from '../../utils/helpers';

const MAX_LENGTH = 8000;
const TYPING_THROTTLE_MS = 2000;
const MAX_FILES = 5;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
// Matches the server's magic-byte allowlist; the server re-checks regardless,
// since an accept attribute is only a file-picker hint.
const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,image/heic,application/pdf';

export default function MessageComposer() {
  const {
    activeConversationId,
    sendMessage,
    sendWithAttachments,
    setTyping,
    connection,
  } = useChat();
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastTypingSent = useRef(0);
  const stopTypingTimer = useRef(null);

  useEffect(() => {
    // Restore whatever was being typed here. Files are deliberately not
    // restored — a File cannot be serialised, and silently dropping an
    // attachment the member thought was queued would be worse than clearing it.
    setValue(loadDraft(activeConversationId));
    setPendingFiles([]);
    setFileError(null);

    const el = textareaRef.current;
    if (el) {
      el.focus();
      // Cursor at the end of the restored text, not the start.
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }
  }, [activeConversationId]);

  // Autosize up to a ceiling, so a long message is visible without the composer
  // eating the whole panel.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [value]);

  useEffect(() => () => clearTimeout(stopTypingTimer.current), []);

  const handleChange = (event) => {
    setValue(event.target.value);
    saveDraft(activeConversationId, event.target.value);

    // Throttled: one typing:start every couple of seconds, not one per keystroke.
    const now = Date.now();
    if (now - lastTypingSent.current > TYPING_THROTTLE_MS) {
      setTyping(activeConversationId, true);
      lastTypingSent.current = now;
    }

    clearTimeout(stopTypingTimer.current);
    stopTypingTimer.current = setTimeout(() => {
      setTyping(activeConversationId, false);
      lastTypingSent.current = 0;
    }, 3000);
  };

  const addFiles = (files) => {
    setFileError(null);
    const incoming = Array.from(files || []);
    if (incoming.length === 0) return;

    // Mirrors the server's caps so a doomed upload fails here instead of after
    // the bytes have already gone over the wire.
    const tooBig = incoming.find((file) => file.size > MAX_FILE_BYTES);
    if (tooBig) {
      setFileError(`"${tooBig.name}" is larger than ${MAX_FILE_BYTES / 1024 / 1024}MB.`);
      return;
    }
    if (pendingFiles.length + incoming.length > MAX_FILES) {
      setFileError(`You can attach at most ${MAX_FILES} files.`);
      return;
    }

    setPendingFiles((prev) => [...prev, ...incoming]);
  };

  const submit = async () => {
    const body = value.trim();
    if ((!body && pendingFiles.length === 0) || sending) return;

    setSending(true);
    clearTimeout(stopTypingTimer.current);
    setTyping(activeConversationId, false);
    lastTypingSent.current = 0;

    const files = pendingFiles;

    // Cleared before the await so the next message can be typed immediately —
    // the optimistic bubble is already on screen.
    setValue('');
    saveDraft(activeConversationId, '');
    setPendingFiles([]);
    setFileError(null);

    try {
      if (files.length > 0) {
        await sendWithAttachments(activeConversationId, files, body);
      } else {
        await sendMessage(activeConversationId, body);
      }
    } catch {
      // The failed bubble in MessageList carries the error state.
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (event) => {
    // Enter sends, Shift+Enter makes a new line.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const disabled = connection === 'expired';
  const remaining = MAX_LENGTH - value.length;
  const canSend = (value.trim() || pendingFiles.length > 0) && !sending && !disabled;

  return (
    <div
      className="border-t border-neutral-300 bg-neutral-100 px-3 py-2.5"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        if (!disabled) addFiles(event.dataTransfer.files);
      }}
    >
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {pendingFiles.map((file, index) => (
            <span
              key={`${file.name}-${index}`}
              className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full
                         bg-highlight border border-primary/15 text-primary text-xs max-w-[180px]"
            >
              <span className="truncate">{file.name}</span>
              <button
                onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                className="p-0.5 rounded-full text-primary/60 hover:text-primary hover:bg-primary/10 shrink-0 transition-colors"
                aria-label={`Remove ${file.name}`}
              >
                <FiX className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {fileError && <p className="text-[11px] text-alert mb-2">{fileError}</p>}

      {/* One field rather than three separate controls: the whole rail lights up
          with the site's accent on focus, the way form inputs do elsewhere. */}
      <div
        className="flex items-end gap-1 rounded-[10px] bg-white border border-neutral-300 p-1.5
                   focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/40 transition-all"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(event) => {
            addFiles(event.target.files);
            // Reset so picking the same file twice in a row still fires change.
            event.target.value = '';
          }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="shrink-0 p-2 rounded-full text-secondary hover:text-primary
                     hover:bg-highlight disabled:opacity-40 transition-colors"
          aria-label="Attach files"
          title="Attach files"
        >
          <FiPaperclip className="w-4 h-4" />
        </button>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
          maxLength={MAX_LENGTH}
          placeholder={disabled ? 'Sign in again to send messages' : 'Write a message, or drop a file…'}
          className="flex-1 resize-none bg-transparent border-0 px-2 py-2 text-sm leading-5
                     text-primary placeholder:text-primary/35 focus:outline-none focus:ring-0"
        />
        <button
          onClick={submit}
          disabled={!canSend}
          className={cn(
            'shrink-0 p-2 rounded-full transition-all',
            canSend
              // The site's send/confirm colour is accent on primary text — white
              // on this yellow does not clear contrast.
              ? 'bg-accent text-primary shadow-sm hover:bg-accent-600 hover:shadow-md active:scale-95'
              : 'bg-primary/10 text-primary/30 cursor-not-allowed'
          )}
          aria-label="Send message"
        >
          <FiSend className="w-4 h-4" />
        </button>
      </div>

      {remaining < 200 && (
        <p className="text-[11px] text-primary/40 mt-1 text-right">{remaining} characters left</p>
      )}
    </div>
  );
}
